import tokens from "@relentless/rmds/v1/tokens.json";

const THEME_KEY = "theme";

export function getInitialTheme() {
  if (typeof window === "undefined") return "light";
  const saved = window.localStorage.getItem(THEME_KEY);
  return saved === "dark" ? "dark" : "light";
}

function setCssVar(name, value) {
  document.documentElement.style.setProperty(name, String(value));
}

function hexToRgb(hex) {
  let h = String(hex).replace(/^#/, "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const int = parseInt(h, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `${r}, ${g}, ${b}`;
}

function setTokenVars(obj, path = []) {
  if (!obj) return;
  Object.entries(obj).forEach(([key, val]) => {
    const nextPath = [...path, key];
    if (val && typeof val === "object" && !Array.isArray(val)) {
      setTokenVars(val, nextPath);
    } else {
      const varName = `--${nextPath.join("-")}`; // unprefixed to match DS styles
      setCssVar(varName, typeof val === "number" ? `${val / 16}rem` : val);
      // Provide -rgb helper for hex colors used in rgba(var(--*-rgb), a)
      if (typeof val === "string" && /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(val)) {
        setCssVar(`${varName}-rgb`, hexToRgb(val));
      }
    }
  });
}

// Dimensions are driven purely by CSS media queries now (no JS)

export function applyTheme(theme) {
  const mode = theme === "dark" ? "dark" : "light";
  const root = document.documentElement;
  root.setAttribute("data-theme", mode);

  // Set only color-related style tokens; avoid overriding responsive text tokens
  const style = tokens.styleGuide?.[mode] || {};
  const styleColorsOnly = Object.fromEntries(
    Object.entries(style).filter(([k]) => !/^text/i.test(k))
  );
  // Set color tokens unprefixed (DS expects --theme*, --brand*)
  setTokenVars(styleColorsOnly);
  // Allow CSS overrides from index.css: skip brand if author opts in
  const allowCssBrandOverrides =
    root.hasAttribute("data-allow-css-brand-overrides") ||
    root.hasAttribute("data-allow-css-overrides");
  if (!allowCssBrandOverrides) {
    setTokenVars(tokens.brand?.[mode]);
  }

  // Back-compat: also set prefixed --color-* for any consumers that use them
  const setPrefixed = (obj, prefix) => {
    if (!obj) return;
    Object.entries(obj).forEach(([k, v]) => setCssVar(`--${prefix}-${k}`, v));
  };
  setPrefixed(styleColorsOnly, "color");
  if (!allowCssBrandOverrides) setPrefixed(tokens.brand?.[mode], "brand");

  // Dimensions are controlled by CSS; do not set via JS

  window.localStorage.setItem(THEME_KEY, mode);

}

export function toggleTheme() {
  const current = getInitialTheme();
  const next = current === "light" ? "dark" : "light";
  applyTheme(next);
  return next;
}

// One-time initialization: sets theme tokens
export function initDesignTokens(options = {}) {
  const { theme = getInitialTheme() } = options || {};
  applyTheme(theme);
  return () => {};
}
